import { MouseEvent, RefObject, useRef } from 'react';

import type { ReCaptchaRef } from 'components/ReCaptcha/ReCaptcha';

const TOKEN_TIMEOUT_MS = 60_000;

export type GetFreshTokenFn = () => Promise<string>;

interface PendingToken {
  resolve: (token: string) => void;
  reject: (error: Error) => void;
  timeoutId: ReturnType<typeof setTimeout>;
}

interface UseCaptchaSubmitParams {
  onSubmit?: (recaptchaToken: string) => Promise<void> | void;
  onMultiStepSubmit?: (getFreshToken: GetFreshTokenFn) => Promise<void> | void;
  onSubmitError?: (error: unknown) => void;
  submitErrorMessage?: string;
  executeErrorMessage?: string;
  captchaErrorMessage?: string;
  resetAfterSubmit?: boolean;
}

interface UseCaptchaSubmitReturn {
  recaptchaRef: RefObject<ReCaptchaRef>;
  handleCaptchaChange: (value: string | null) => Promise<void>;
  handleCaptchaError: (error: Error | undefined) => void;
  handleCaptchaExpired: () => void;
  handleExecuteClick: (event: MouseEvent<HTMLElement>) => Promise<void>;
  handleFormSubmit: () => Promise<void>;
}

const useCaptchaSubmit = ({
  onSubmit,
  onMultiStepSubmit,
  onSubmitError,
  submitErrorMessage = 'Error in handleSubmit:',
  executeErrorMessage = 'Error executing reCAPTCHA:',
  captchaErrorMessage = 'reCAPTCHA error:',
  resetAfterSubmit = true
}: UseCaptchaSubmitParams): UseCaptchaSubmitReturn => {
  const recaptchaRef = useRef<ReCaptchaRef>(null);
  const pendingTokenRef = useRef<PendingToken | null>(null);
  const isExecutingRef = useRef(false);

  const resetCaptcha = (): void => {
    recaptchaRef.current?.reset();
  };

  const resolvePendingToken = (token: string): void => {
    const pending = pendingTokenRef.current;

    if (!pending) {
      return;
    }

    clearTimeout(pending.timeoutId);
    pendingTokenRef.current = null;
    pending.resolve(token);
  };

  const rejectPendingToken = (error: Error): void => {
    const pending = pendingTokenRef.current;

    if (!pending) {
      return;
    }

    clearTimeout(pending.timeoutId);
    pendingTokenRef.current = null;
    pending.reject(error);
  };

  const requestFreshToken = (): Promise<string> =>
    new Promise((resolve, reject) => {
      if (pendingTokenRef.current) {
        reject(new Error('Captcha token request already pending'));
        return;
      }

      resetCaptcha();

      const timeoutId = setTimeout(() => {
        if (!pendingTokenRef.current) {
          return;
        }

        rejectPendingToken(new Error('Captcha token request timed out'));
        resetCaptcha();
      }, TOKEN_TIMEOUT_MS);

      pendingTokenRef.current = { resolve, reject, timeoutId };

      try {
        recaptchaRef.current?.execute();
      } catch (error) {
        console.error(executeErrorMessage, error);
        rejectPendingToken(error instanceof Error ? error : new Error(String(error)));
        resetCaptcha();
      }
    });

  const handleCaptchaChange = async (value: string | null): Promise<void> => {
    if (!value) {
      return;
    }

    if (pendingTokenRef.current) {
      resolvePendingToken(value);

      if (resetAfterSubmit) {
        resetCaptcha();
      }

      return;
    }

    // Legacy path: execute() was called manually without handleExecuteClick.
    // Supports components that call recaptchaRef.current.execute() directly.
    if (onSubmit) {
      try {
        await onSubmit(value);
      } catch (error) {
        console.error(submitErrorMessage, error);
        onSubmitError?.(error);
      } finally {
        if (resetAfterSubmit) {
          resetCaptcha();
        }
      }
    }
  };

  const runSubmit = async (): Promise<void> => {
    if (isExecutingRef.current || (!onMultiStepSubmit && !onSubmit)) {
      return;
    }

    isExecutingRef.current = true;

    try {
      if (onMultiStepSubmit) {
        await onMultiStepSubmit(requestFreshToken);
      } else if (onSubmit) {
        const token = await requestFreshToken();
        await onSubmit(token);
      }
    } catch (error) {
      console.error(submitErrorMessage, error);
      onSubmitError?.(error);
    } finally {
      isExecutingRef.current = false;
      resetCaptcha();
    }
  };

  const handleFormSubmit = async (): Promise<void> => {
    await runSubmit();
  };

  const handleExecuteClick = async (
    event: MouseEvent<HTMLElement>
  ): Promise<void> => {
    event.preventDefault();
    await runSubmit();
  };

  const handleCaptchaExpired = (): void => {
    rejectPendingToken(new Error('reCAPTCHA expired'));
    resetCaptcha();
    isExecutingRef.current = false;
  };

  const handleCaptchaError = (error: Error | undefined): void => {
    console.error(captchaErrorMessage, error);
    rejectPendingToken(error ?? new Error('reCAPTCHA error'));
    resetCaptcha();
    isExecutingRef.current = false;
  };

  return {
    recaptchaRef,
    handleCaptchaChange,
    handleCaptchaError,
    handleCaptchaExpired,
    handleExecuteClick,
    handleFormSubmit
  };
};

export default useCaptchaSubmit;
